import { UNCLASSIFIED_STYLE } from '@/utils/feed';

/**
 * feed-genre-filter — the closed list of canonical music styles.
 *
 * Not Deezer's 23 roots and not musicmap's 23 super-genres. From musicmap we
 * kept the discipline of reduction (600 → 230 → 23, explicitly "for
 * comprehensibility") and dropped its cut: that map spends 8 categories on rock
 * and 1 on rap, and has no classical, no world, no soundtrack — it is an atlas
 * of lineage, and this is a tap.
 *
 * Each key covers one Deezer root, which is what keeps "unclassified" a signal
 * that a genre still needs attaching rather than a dumping ground.
 *
 * ⚠ MUST stay in sync with `racore-back/src/constants/genre-styles.ts` — the
 * backend rejects an unknown style with a 400. Two corrections came from
 * fetching the real `GET api.deezer.com/genre`: `folk` is a Deezer root and had
 * to be added (unmapped it would have fallen into "unclassified"), and
 * `country` has NO Deezer root — it is kept but will stay empty until a
 * sub-genre surfaces it. Labels live
 * in `translations/*.json`: the UI never shows a raw Deezer label, so the
 * displayed set is closed and small.
 */
export const STYLE_KEYS = [
  'rap',
  'pop',
  'rock',
  'electro',
  'rnbSoul',
  'jazz',
  'classical',
  'metal',
  'reggae',
  'blues',
  'folk',
  'country',
  'soundtrack',
  'kids',
  'world',
] as const;

export type StyleKey = (typeof STYLE_KEYS)[number];

/** The emoji carries the chip in languages where the word does not travel. */
export const STYLE_EMOJI: Record<StyleKey | typeof UNCLASSIFIED_STYLE, string> = {
  rap: '🎤',
  pop: '✨',
  rock: '🎸',
  electro: '🎛️',
  rnbSoul: '💜',
  jazz: '🎷',
  classical: '🎻',
  metal: '🤘',
  reggae: '🌴',
  blues: '🎺',
  folk: '🪕',
  country: '🤠',
  soundtrack: '🎬',
  kids: '🧸',
  world: '🌍',
  [UNCLASSIFIED_STYLE]: '⬜',
};
